import { Client as BelfastClient } from '@kaizenplatform/belfast';
import { FileReference } from '@kaizenplatform/belfast-entity';
import Store from 'repatch';

import State from '../core/State';
import { GetOfferByIdQuery } from '../graphql/generated/graphql_offer';
import ErrorPresenter from '../service/ErrorPresenter';
import FilesWebApi from '../service/persistent/FilesWebApi';
import OfferWebApi from '../service/persistent/offer/OfferWebApi';
import ProjectChangeWebApi from '../service/persistent/ProjectChangeWebApi';
import ProjectWebApi, { ProjectType } from '../service/persistent/ProjectWebApi';
import { setProjectChange } from '../thunk/project';
import { getAssetFiles, getBriefFileBucketIds, getBriefFiles } from '../utility/graphql/project';
import { dispose, finishFetchingOffer, setOfferWithFiles, startFetchingOffer } from './../thunk/offer';
import { setProject } from './../thunk/project';

class ShowOfferUsecase {
  private offerWebApi: OfferWebApi;
  private belfastClient: BelfastClient;
  private store: Store<State>;
  private errorPresenter: ErrorPresenter;
  private readonly projectChangeWebApi: ProjectChangeWebApi;
  private projectWebApi: ProjectWebApi;
  private readonly filesWebApi: FilesWebApi;

  constructor({
    offerWebApi,
    belfastClient,
    store,
    errorPresenter,
    projectChangeWebApi,
    projectWebApi,
    filesWebApi,
  }: {
    store: Store<State>;
    belfastClient: BelfastClient;
    offerWebApi: OfferWebApi;
    errorPresenter: ErrorPresenter;
    projectChangeWebApi: ProjectChangeWebApi;
    projectWebApi: ProjectWebApi;
    filesWebApi: FilesWebApi;
  }) {
    this.store = store;
    this.offerWebApi = offerWebApi;
    this.belfastClient = belfastClient;
    this.errorPresenter = errorPresenter;
    this.projectChangeWebApi = projectChangeWebApi;
    this.projectWebApi = projectWebApi;
    this.filesWebApi = filesWebApi;
  }

  public prepare = async (offerId: string): Promise<void> => {
    const user = this.store.getState().authentication.signedInUser;
    if (!user) {
      throw new Error();
    }

    this.store.dispatch(startFetchingOffer());

    let offer: GetOfferByIdQuery['offer'];
    let project: ProjectType;
    const language = this.store.getState().language;

    try {
      offer = await this.offerWebApi.getById(offerId);
      project = await this.projectWebApi.getByCreationSetId(offer.jobId, language);
    } catch (err) {
      this.errorPresenter.showAsFatal(new Error('message.fetch_offer_has_failed'));

      this.store.dispatch(finishFetchingOffer());

      return;
    }

    const briefFiles = await (async () => {
      const creativeBriefValue = project.creativeBriefValue;
      if (creativeBriefValue) {
        const briefFileBucketIds = getBriefFileBucketIds(
          project,
          (key) => creativeBriefValue.values.get(key) as string,
        );
        return await getBriefFiles(project, briefFileBucketIds, this.belfastClient, this.filesWebApi);
      } else {
        return new Map<string, FileReference[]>();
      }
    })();

    const assetFiles = await getAssetFiles(project, this.belfastClient, this.filesWebApi);

    const projectChange = await this.projectChangeWebApi.getById(project);
    this.store.dispatch(setOfferWithFiles(offer, assetFiles, briefFiles));
    this.store.dispatch(setProject(project));
    if (projectChange) {
      this.store.dispatch(setProjectChange(projectChange));
    }
    this.store.dispatch(finishFetchingOffer());
  };

  public disposeOffer = (): void => {
    this.store.dispatch(dispose());
  };
}

export default ShowOfferUsecase;
